import { useEffect, useState } from "react";
import { Menu, LogOut, ChevronDown, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SidebarContent } from "@/components/layout/Sidebar";
import { WithTooltip } from "@/components/common/Tooltips";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";

const ROLE_LABEL = {
  super_admin: "Super Administrador", admin_entidad: "Administrador de Entidad",
  oficial_cumplimiento: "Oficial de Cumplimiento", analista_pld: "Analista PLD",
  ejecutivo_credito: "Ejecutivo de Crédito", cobranza: "Cobranza", lectura: "Solo lectura",
  cargador_listas: "Cargador de Listas (plataforma)",
};

export function Topbar() {
  const { user, logout } = useAuth();
  const [entidad, setEntidad] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (user?.entity_id) {
      api.get(`/entidades/${user.entity_id}`).then((r) => setEntidad(r.data)).catch(() => {});
    }
  }, [user]);

  const initials = (user?.name || "U").split(" ").map((p) => p[0]).slice(0, 2).join("").toUpperCase();

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b border-slate-200 bg-white px-4 md:px-8">
      <div className="flex items-center gap-3">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <WithTooltip text="Abrir el menú de navegación" side="bottom">
              <Button variant="ghost" size="icon" className="lg:hidden" data-testid="mobile-menu-btn" aria-label="Abrir el menú de navegación">
                <Menu className="h-5 w-5" />
              </Button>
            </WithTooltip>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 border-none p-0">
            <SidebarContent onNavigate={() => setOpen(false)} />
          </SheetContent>
        </Sheet>
        <div className="hidden items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-1.5 sm:flex">
          <Building2 className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-slate-700" data-testid="current-entity">
            {/* sep-2026 · los usuarios de PLATAFORMA no pertenecen a ninguna entidad: antes
                se les pintaba "Mi entidad", que no existe para ellos. */}
            {user?.role === "super_admin" || !user?.entity_id
              ? "Vista Plataforma"
              : entidad?.nombre_comercial || entidad?.razon_social || "Mi entidad"}
          </span>
        </div>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button data-testid="user-menu-btn" className="flex items-center gap-2 rounded-md px-2 py-1.5 transition-colors hover:bg-slate-100">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-xs font-semibold text-white">
              {initials}
            </div>
            <div className="hidden text-left sm:block">
              <p className="text-sm font-medium leading-tight text-slate-800">{user?.name}</p>
              <p className="text-[11px] leading-tight text-slate-500">{ROLE_LABEL[user?.role] || user?.role}</p>
            </div>
            <ChevronDown className="h-4 w-4 text-slate-400" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel>
            <p className="text-sm font-medium">{user?.name}</p>
            <p className="text-xs font-normal text-slate-500">{user?.email}</p>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem data-testid="logout-btn" onClick={logout} className="text-red-600 focus:text-red-600">
            <LogOut className="mr-2 h-4 w-4" /> Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
